import React, { useEffect, useState } from "react";
import { Percent, ChevronLeft, ChevronRight, Building2, CalendarDays, FileDown, Users } from "lucide-react";
import api, { TRY, NUM, trDate, weekendRow } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import VatBulkDialog from "@/components/VatBulkDialog";
import SearchBox, { matches } from "@/components/SearchBox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const shiftMonth = (m, n) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

export default function Vat() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = useState("");
  const shownCustomers = (data?.customers || []).filter((c) => matches(search, c.name, c.short_name, c.vat_rate ? `%${c.vat_rate}` : "kdv yok"));
  const load = () => api.get("/reports/vat", { params: { month } }).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  return (
    <div className="space-y-6" data-testid="vat-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">KDV Takibi</h1>
          <p className="text-sm text-muted-foreground mt-1">KDV uygulanan müşterilerde kişi başı fiyata %5 veya %10 eklenir; KDV ciroya dahil edilir, maliyetlerde ayrı gösterilir.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="vat-bulk-btn" className="rounded-full h-9 gap-2"><Users className="h-4 w-4" /> Toplu KDV Ayarı</Button>
          <a href={`${BACKEND}/api/reports/vat/pdf?month=${month}`} target="_blank" rel="noreferrer" data-testid="vat-pdf-link"><Button variant="outline" className="rounded-full h-9 gap-2"><FileDown className="h-4 w-4" /> KDV Raporu PDF</Button></a>
          <div className="flex items-center gap-1">
            <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="vat-prev" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="vat-month" className="h-9 w-40 rounded-md border border-input bg-background px-2 text-sm font-mono" />
            <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="vat-next" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      <VatBulkDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onSaved={load} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Net Ciro (KDV hariç)" value={TRY(data?.net)} testId="vat-kpi-net" />
        <Kpi label="Toplam KDV" value={TRY(data?.vat)} tone="text-primary" testId="vat-kpi-vat" />
        <Kpi label="Brüt Ciro (KDV dahil)" value={TRY(data?.gross)} tone="text-emerald-400" testId="vat-kpi-gross" />
        <Kpi label="KDV'li Müşteri" value={`${data?.vat_customers ?? 0}`} sub={`${monthLabel(month)} · ${NUM(data?.headcount ?? 0)} kişi`} testId="vat-kpi-customers" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card className="overflow-hidden" data-testid="vat-customers">
          <div className="p-5 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /> Müşteri Bazında</h3>
            <SearchBox value={search} onChange={setSearch} placeholder="Müşteri ara…" testId="vat-search-input" className="w-full sm:w-[240px]" />
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-y border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2.5">Müşteri</th><th className="px-4 py-2.5 text-right">Kişi</th><th className="px-4 py-2.5 text-right">Net</th><th className="px-4 py-2.5 text-right">KDV</th><th className="px-4 py-2.5 text-right">Brüt</th>
            </tr></thead>
            <tbody>
              {!data?.customers?.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground" data-testid="vat-customers-empty">Bu ay kayıt yok</td></tr>}
              {data?.customers?.length > 0 && shownCustomers.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm" data-testid="vat-search-empty">Aramayla eşleşen müşteri yok</td></tr>}
              {shownCustomers.map((c) => (
                <tr key={c.customer_id} className="border-b border-border/60" data-testid={`vat-cust-${c.customer_id}`}>
                  <td className="px-4 py-2.5 font-medium">{c.short_name || c.name} {c.vat_rate > 0 ? <span className="ml-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold">%{c.vat_rate} KDV</span> : <span className="ml-1 text-[10px] text-muted-foreground">KDV yok</span>}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{NUM(c.headcount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{TRY(c.net)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-primary font-semibold">{TRY(c.vat)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{TRY(c.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="overflow-hidden" data-testid="vat-days">
          <div className="p-5 pb-3"><h3 className="font-heading text-lg font-semibold flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Gün Bazında</h3></div>
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-y border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">Tarih</th><th className="px-4 py-2.5 text-right">Net</th><th className="px-4 py-2.5 text-right">KDV</th><th className="px-4 py-2.5 text-right">Brüt</th>
              </tr></thead>
              <tbody>
                {!data?.days?.length && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Bu ay kayıt yok</td></tr>}
                {data?.days?.map((d) => (
                  <tr key={d.date} className={"border-b border-border/60" + weekendRow(d.date)} data-testid={`vat-day-${d.date}`}>
                    <td className="px-4 py-2 font-mono text-xs">{trDate(d.date)}</td>
                    <td className="px-4 py-2 text-right font-mono">{TRY(d.net)}</td>
                    <td className="px-4 py-2 text-right font-mono text-primary">{TRY(d.vat)}</td>
                    <td className="px-4 py-2 text-right font-mono">{TRY(d.gross)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, sub, tone = "", testId }) => (
  <Card className="p-5" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Percent className="h-3 w-3" /> {label}</div>
    <div className={`font-mono text-2xl font-bold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </Card>
);
